import type { CardSummary } from "@libs/buyer-identity-client";

/**
 * The card slot's rules, whichever checkout's card it is: which card is
 * selected, and whether the secure fields need anything from the buyer
 * first. Pure: the web and native card slots both draw from it, through the
 * purchase's own slot (`purchaseCardSlotOf`), whose payment policy says
 * which cards the checkout takes.
 *
 * A saved card's security code is held only briefly; one that would not
 * last the browser's run (more than CVC_FRESH_MS left) asks for the code
 * again, in its own row. "Use another card" swaps the saved cards for the
 * new-card fields. A refused card is never chosen for the buyer and never
 * usable: shown, it says why (`refusal`), and the chip does not choose it.
 */

/** How long a saved card's code must still be held for a run to use it. */
export const CVC_FRESH_MS = 240000;

export type CardChoice = {
  /** A saved card the buyer picked (`null`: the first the checkout takes). */
  cardId: string | null;
  /** The buyer is typing another card instead. */
  newCard: boolean;
};

export const NO_CHOICE: CardChoice = { cardId: null, newCard: false };

export const cvcFresh = (card: CardSummary | null | undefined, now: number) =>
  !!card?.cvcExpiresAt &&
  new Date(card.cvcExpiresAt).getTime() > now + CVC_FRESH_MS;

export type CardSlot = {
  /** The saved card selected for preparation, or null for a new card. */
  selected: CardSummary | null;
  /** Its code is fresh: nothing to type. */
  ready: boolean;
  /** What the secure fields collect: a new card, a saved card's code, or
   *  nothing at all. */
  fields: "card" | "cvc" | null;
  /** Keyed so the secure fields reload for another card. */
  fieldsKey: string;
  /** The saved card the chip shows: the selected one, else the first the
   *  checkout takes to go back to, else the newest saved one. */
  chip: CardSummary | null;
  /** Why the checkout does not take the chip's card, to say beside it; null
   *  when it does. The chip does not choose a refused card, and a refused
   *  card selected is not usable. */
  refusal: string | null;
};

/** Whether the chip may choose its card: always to give it up, and to take
 *  it only when the checkout takes it. */
export const chipChoosable = (slot: CardSlot) =>
  slot.selected !== null || slot.refusal === null;

/** Continue is possible once the details are complete and the card is
 *  usable: one the checkout takes, fresh as it is, or the secure fields for
 *  it valid. */
export const cardUsable = (
  slot: CardSlot,
  fieldsValid: { key: string; valid: boolean } | null,
) =>
  !(slot.selected && slot.refusal) &&
  (slot.ready ||
    (fieldsValid !== null &&
      fieldsValid.key === slot.fieldsKey &&
      fieldsValid.valid));
